'use client';

import type { PopperChildrenProps } from "@mui/base/Popper/Popper.types";
import Box from '@mui/material/Box';
import Popper from '@mui/material/Popper';
import { useEffect, useState } from 'react';

import type { BoxProps } from '@mui/material';
import type { PopperProps } from '@mui/material/Popper';
import type { ComponentType, FC, MouseEventHandler, ReactNode } from 'react';

export interface HoverPopperProps
  extends Omit<PopperProps, 'open' | 'component'>, Omit<BoxProps, 'color' | 'ref' | 'children'>
{
  open?: PopperProps['open'];
  placement: PopperProps['placement'];
  component: ComponentType<{ open?: boolean }>;
  // children?: ReactNode | ((props: PopperChildrenProps) => ReactNode);
  anchorEl?: HTMLElement | null;
  onOpen?: MouseEventHandler;
  onClose?: MouseEventHandler;
}

/**
 * HoverPopper
 * Popper opening on mouseover
 * dropdown
 * no style
 * requires a render-component
 */

export const HoverPopper: FC<HoverPopperProps> = ({
  component: RenderComponent,
  placement = 'bottom',
  children = null,
  sx,
  anchorEl: initialAnchorEl = null,
  onOpen = undefined,
  onClose = undefined,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>( initialAnchorEl );

  const handleOpen: MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl( event.currentTarget );
    onOpen?.( event );
  };

  const handleClose: MouseEventHandler<HTMLElement> = (event) => {
    setAnchorEl( null );
    onClose?.( event );
  };

  const open = Boolean( anchorEl );

  const id = open ? 'simple-popper' : undefined;

  useEffect( () => {
    setAnchorEl( initialAnchorEl );
  }, [initialAnchorEl] );

  return (
    <Box sx={sx} onMouseLeave={handleClose} onMouseEnter={handleOpen}>
      <RenderComponent open={open} aria-describedby={id} />
      <Popper
        disablePortal={true}
        placement={placement}
        id={id}
        open={open}
        anchorEl={anchorEl}
        {...props}
      >
        {children}
      </Popper>
    </Box>
  );
};
